/**
 * Access token em **memória do módulo**, nunca em `localStorage` nem em cookie
 * legível.
 *
 * `localStorage` sobrevive ao fechamento da aba e é lido por qualquer script da
 * página: um XSS levaria a credencial embora. Em memória, o custo é perder a sessão
 * ao recarregar — e é exatamente para isso que existe o refresh em cookie httpOnly,
 * que o JavaScript não alcança.
 */

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

const CSRF_COOKIE = 'troig_csrf';

/**
 * Lê o cookie de CSRF para repetir o valor no header (double submit).
 *
 * Este cookie é legível de propósito — não é credencial. Quem não tem o cookie de
 * sessão não ganha nada com ele, e a proteção vem justamente de outro site não
 * conseguir *ler* o valor para montar o header.
 */
export function getCsrfToken(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]*)`));

  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/**
 * Como o cliente HTTP renova a sessão quando o access token expira.
 *
 * ## Por que um ponto de registro, e não um `import` direto
 *
 * Quem sabe renovar é `features/auth`, que importa `lib/http` para falar com a API. Se
 * `lib/http` importasse a renovação de volta, o ciclo se fecharia. O provedor de sessão
 * **registra** a função aqui na montagem, e o cliente HTTP chama sem conhecer quem atende.
 *
 * A camada de baixo continua sem depender da de cima, e o provedor continua sendo o único
 * lugar que sabe mexer no estado da sessão — inclusive para derrubá-la quando a renovação
 * falha, que é o que leva a pessoa de volta ao login em vez de a uma tela quebrada.
 */
type RenovadorDeSessao = () => Promise<string | null>;

let renovador: RenovadorDeSessao | null = null;

export function definirRenovadorDeSessao(fn: RenovadorDeSessao | null): void {
  renovador = fn;
}

/**
 * Renova e devolve o novo access token, ou `null` se não deu.
 *
 * Nunca lança: quem chama está no meio de tratar um 401, e uma exceção aqui trocaria "sua
 * sessão expirou" por um erro de rede sem sentido para quem lê.
 */
export async function renovarAccessToken(): Promise<string | null> {
  if (!renovador) return null;

  try {
    return await renovador();
  } catch {
    return null;
  }
}
